import { useState } from 'react'
import { appUrl } from '@astro/config'
import { useOrders } from '../useDashboard'


/* Inline lucide-style icons — the card chrome uses these instead of emoji. */
const Icon = ({ children, className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
)
const BagIcon = (props) => (
  <Icon {...props}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></Icon>
)
const CalendarIcon = (props) => (
  <Icon {...props}><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>
)
const TagIcon = (props) => (
  <Icon {...props}><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" /><circle cx="7.5" cy="7.5" r=".5" fill="currentColor" /></Icon>
)
const CardIcon = (props) => (
  <Icon {...props}><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></Icon>
)
const GemIcon = (props) => (
  <Icon {...props}><path d="M6 3h12l4 6-10 13L2 9Z" /><path d="M11 3 8 9l4 13 4-13-3-6" /><path d="M2 9h20" /></Icon>
)

const pill = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ring-1 ring-inset ring-black/5'

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-stone-100 bg-white overflow-hidden animate-pulse">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 sm:py-4 border-b border-stone-100">
        <div className="w-10 h-10 rounded-xl bg-stone-100 shrink-0" />
        <div className="space-y-2">
          <div className="h-4 w-44 rounded bg-stone-100" />
          <div className="h-3 w-28 rounded bg-stone-100" />
        </div>
        <div className="ml-auto flex gap-2">
          <div className="h-7 w-20 rounded-full bg-stone-100" />
          <div className="h-7 w-28 rounded-full bg-stone-100" />
        </div>
      </div>
      <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5 sm:py-4">
        <div className="w-16 h-16 rounded-xl bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-4 w-2/3 rounded bg-stone-100" />
          <div className="h-3 w-1/3 rounded bg-stone-100" />
        </div>
        <div className="h-4 w-16 rounded bg-stone-100 shrink-0" />
      </div>
      <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-t border-stone-100 bg-stone-50/50">
        <div className="h-3 w-36 rounded bg-stone-100" />
        <div className="h-6 w-28 rounded bg-stone-100" />
      </div>
    </div>
  )
}

function OrderItem({ item }) {
  return (
    <div className="flex items-center gap-4 py-3.5 group/item">
      {item.img ? (
        <img
          src={item.img}
          alt={item.name}
          className="w-16 h-16 rounded-xl object-cover bg-amber-50 ring-1 ring-stone-100 shrink-0 transition-transform duration-300 group-hover/item:scale-105"
        />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-amber-50 ring-1 ring-amber-100/60 flex items-center justify-center text-amber-300 shrink-0">
          <GemIcon className="w-7 h-7" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-base font-semibold text-astroDark truncate">{item.name}</p>
        <p className="text-sm text-stone-400 mt-1">
          Qty {item.qty} <span className="text-stone-300 mx-1">×</span> <span className="num">{item.unitPrice}</span>
        </p>
      </div>
      <p className="num text-base font-bold text-astroDark shrink-0">{item.lineTotal}</p>
    </div>
  )
}

const ChevronIcon = (props) => (
  <Icon {...props}><path d="m6 9 6 6 6-6" /></Icon>
)

/* Sub-tabs over the order list — `key` is the API's ?status= group. */
const ORDER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'returned', label: 'Returns & Refunds' },
]

function OrderCard({ order: o, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-2xl border border-amber-900/10 bg-white overflow-hidden shadow-sm hover:shadow-lg hover:shadow-amber-900/5 hover:border-amber-200/80 transition-all duration-300">
      {/* header — order identity left, status + tracking action right. The whole
          bar toggles the card; Track Order still navigates. */}
      <div
        onClick={() => setOpen((v) => !v)}
        className={`flex flex-wrap items-center gap-x-4 gap-y-3 px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-r from-amber-50/70 via-white to-white cursor-pointer select-none ${open ? 'border-b border-stone-100' : ''}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-amber-100/80 text-amber-700 flex items-center justify-center shrink-0">
            <BagIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="num text-base font-bold text-astroDark truncate">Order {o.number}</p>
            <div className="flex items-center gap-3 mt-0.5 text-sm text-stone-400">
              {o.date && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarIcon className="w-3.5 h-3.5" /> {o.date}
                </span>
              )}
              {o.coupon && (
                <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-purple-500">
                  <TagIcon className="w-3.5 h-3.5" /> {o.coupon}
                </span>
              )}
              {/* collapsed: the essentials without opening the card */}
              {!open && (
                <span className="num text-stone-500">
                  {o.itemCount} item{o.itemCount !== 1 ? 's' : ''} · <span className="font-bold text-astroDark">{o.total}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2.5">
          <span className={`${pill} ${o.cls}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
            {o.status}
          </span>
          {/* The tracking page lives in the shop app — absolute cross-app URL so
              this shared tab works from every app that mounts the dashboard.
              o.number is display-formatted with a leading "#" — strip it, or
              the browser treats the rest of the URL as a fragment. */}
          <a
            // href={`${appUrl('shop')}/orders/${encodeURIComponent(String(o.number).replace(/^#/, ''))}`}
            href={`${appUrl('shop')}/orders/${String(o.number).slice(1)}`}
            onClick={(e) => e.stopPropagation()}
            className="group/track inline-flex items-center gap-1.5 rounded-full border border-amber-300/70 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-700 shadow-sm hover:bg-amber-500 hover:border-amber-500 hover:text-white transition-all duration-300"
          >
            Track Order
            <Icon className="w-3.5 h-3.5 transition-transform group-hover/track:translate-x-0.5"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Icon>
          </a>
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Collapse order' : 'Expand order'}
            onClick={(e) => {
              e.stopPropagation()
              setOpen((v) => !v)
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-stone-400 hover:bg-amber-50 hover:text-amber-700 transition-colors"
          >
            <ChevronIcon className={`w-4 h-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* body — slides open/closed (grid-rows 0fr <-> 1fr animates the height) */}
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
      <div className="overflow-hidden min-h-0">
      {/* every item in the order, with its own thumbnail and line total */}
      <div className="px-4 sm:px-5 divide-y divide-stone-100/80">
        {o.items.map((item) => <OrderItem key={item.key} item={item} />)}
      </div>

      {o.notes && (
        <p className="mx-5 mb-3 border-l-2 border-amber-200 pl-3 text-sm italic text-stone-500 truncate">“{o.notes}”</p>
      )}

      {/* footer — payment on the left, money summary on the right */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-x-6 gap-y-3 px-4 sm:px-5 py-3.5 sm:py-4 border-t border-stone-100 bg-stone-50/60">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Payment</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-astroDark">
              <CardIcon className="w-4 h-4 text-stone-400" /> {o.payMethod || '—'}
            </span>
            {o.payStatus && (
              <span className={`${pill} px-2.5 py-0.5 ${o.payCls}`}>{o.payStatus}</span>
            )}
          </div>
          {o.txnId && (
            <p className="num text-xs text-stone-400 mt-1.5 truncate" title={o.txnId}>Txn · {o.txnId}</p>
          )}
        </div>

        <div className="sm:text-right shrink-0">
          {(o.gst || o.shipping || o.savings) && (
            <div className="text-xs text-stone-400 space-y-1 mb-2 pb-2 border-b border-dashed border-stone-200">
              {o.subtotal && <p>Subtotal <span className="num text-stone-500 ml-1">{o.subtotal}</span></p>}
              {o.gst && <p>GST <span className="num text-stone-500 ml-1">{o.gst}</span></p>}
              {o.shipping && <p>Shipping <span className="num text-stone-500 ml-1">{o.shipping}</span></p>}
              {o.savings && <p className="text-green-600 font-semibold">You saved <span className="num ml-1">{o.savings}</span></p>}
            </div>
          )}
          <div className="flex sm:justify-end items-baseline gap-2.5">
            <span className="text-xs uppercase tracking-wider text-stone-400">
              {o.itemCount} item{o.itemCount !== 1 ? 's' : ''} · Total
            </span>
            <span className="num text-2xl font-bold text-astroDark leading-tight">{o.total}</span>
          </div>
        </div>
      </div>
      </div>
      </div>
    </div>
  )
}

export default function OrdersTab() {
  const [page, setPage] = useState(1)
  const [tab, setTab] = useState('active')
  const { orders, hasOrders, isLoading, isFetching, isError, refetch, lastPage, total, counts } = useOrders(page, tab)
  const tabLabel = ORDER_TABS.find((t) => t.key === tab)?.label ?? 'All'

  const switchTab = (key) => {
    setTab(key)
    setPage(1)
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl text-astroDark font-royal">Shop Orders</h3>
        {hasOrders && total > 0 && (
          <span className="text-xs uppercase tracking-wider text-stone-400">{total} order{total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* sub-tabs by order status; counts come with every list response */}
      <div role="tablist" aria-label="Order status" className="flex gap-2 overflow-x-auto pb-1 mb-5 -mx-1 px-1">
        {ORDER_TABS.map((t) => {
          const active = t.key === tab
          const count = counts?.[t.key]
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => switchTab(t.key)}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all duration-200 ${
                active
                  ? 'bg-astroDark text-white border-astroDark shadow-sm'
                  : 'bg-white text-stone-500 border-stone-200 hover:border-amber-300 hover:text-amber-700'
              }`}
            >
              {t.label}
              {typeof count === 'number' && (
                <span className={`num rounded-full px-1.5 min-w-[1.25rem] text-[10px] leading-5 ${active ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-500'}`}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : isError ? (
        <div className="py-12 text-center">
          <div className="text-4xl mb-3 opacity-40">🛰️</div>
          <p className="text-base font-semibold text-astroDark mb-1">Couldn’t load your orders</p>
          <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching your order history.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : !hasOrders ? (
        <div className="py-12 text-center">
          <div className="text-4xl mb-3 opacity-40">🛍️</div>
          {tab === 'all' ? (
            <>
              <p className="text-base font-semibold text-astroDark mb-1">No orders yet</p>
              <p className="text-sm text-stone-400">Your Divine Shop purchases will appear here.</p>
            </>
          ) : (
            <p className="text-base font-semibold text-astroDark">No {tabLabel.toLowerCase()} orders</p>
          )}
        </div>
      ) : (
        <>
          {/* keyed by tab+page so cards re-mount (first one open) on every switch */}
          <div key={`${tab}-${page}`} className={`space-y-4 transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
            {orders.map((o, i) => <OrderCard key={o.key} order={o} defaultOpen={i === 0} />)}
          </div>

          {lastPage > 1 && (
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-100">
              <p className="text-xs uppercase tracking-wider text-stone-400">
                Page {page} of {lastPage}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => p - 1)}
                  className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  disabled={page >= lastPage || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
